import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { ExchangeTimeSeriesResponse } from "@app/components/common/currency-converter/types";
import { getAlpianExchange } from "@app/components/common/currency-converter/helpers";
import { Currency } from "@app/types/currencies";
import { Period } from "./types";
import { DEFAULT_STATE, TIME_RANGE_OFFSET } from "./constants";

export const useHistoricalData = (baseCurrency: Currency, period: Period) => {
  const [data, setData] = useState(DEFAULT_STATE);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTimeSeries = async (baseCurrency: Currency, period: Period) => {
      setData({ isLoading: true, isError: false, rates: null });

      try {
        const startDate = TIME_RANGE_OFFSET[period]().format("YYYY-MM-DD");
        const endDate = dayjs.utc().format("YYYY-MM-DD");

        const response = await fetch(
          `https://api.exchangeratesapi.io/v1/timeseries?access_key=${
            process.env.GATSBY_EXCHANGE_RATE_API_KEY
          }&base=${baseCurrency}&symbols=${Object.values(
            Currency,
          )}&start_date=${startDate}&end_date=${endDate}`,
          { signal: controller.signal },
        );

        if (!response.ok) {
          throw new Error("Failed to get data.");
        }

        const data = (await response.json()) as ExchangeTimeSeriesResponse;

        setData({
          rates: Object.entries(data.rates).reduce((result, [date, rates]) => {
            const midDay = dayjs.utc(`${date} 12:00 PM`);

            return {
              ...result,
              [date]: Object.entries(rates).reduce(
                (result, [currency, rate]) => ({
                  ...result,
                  [currency]:
                    currency === baseCurrency
                      ? rate
                      : getAlpianExchange(rate, midDay.unix()),
                }),
                {},
              ),
            };
          }, {}),
          isError: false,
          isLoading: false,
        });
      } catch (error) {
        setData({
          rates: null,
          isLoading: false,
          isError: true,
        });
      }
    };

    fetchTimeSeries(baseCurrency, period);

    return () => {
      controller.abort();
    };
  }, [baseCurrency, period]);

  return data;
};
